// ═══════════════════════════════════════════════════════════════════════════════
// Task Form Page (Create/Edit - Employer)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../../components/ui/AppButton';
import { useToast } from '../../components/ui/ToastProvider';
import { ApiClientError, taskApi } from '../../lib/api';
import { isLimitReachedError } from '../../lib/limitError';
import { mapApiClientErrorToMessage } from '../../i18n/mapApiClientError';
import {
  MONTHLY_KIND,
  RECURRENCE_TYPE,
  TASK_KIND,
  isHelperReminderEnabled,
  type MonthlyKind,
  type RecurrenceType,
} from '@fdh/shared';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { NativeTimeInput } from '../../components/NativeTimeInput';
import { HelperDueReminderCheckbox } from '../../components/employer/HelperDueReminderCheckbox';
import {
  emptyMonthlySchedulePatch,
  TaskMonthlyScheduleControls,
} from '../../components/employer/TaskMonthlyScheduleControls';

/** Matches IsoDatePicker trigger (e.g. Occurrence date) in light and dark mode */
const TASK_FORM_FIELD_BASE_CLASS =
  'block w-full min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const TASK_FORM_FIELD_CLASS = `mt-1 ${TASK_FORM_FIELD_BASE_CLASS}`;

const DAYS = [0, 1, 2, 3, 4, 5, 6]; // Sun-Sat
const DAY_KEYS = [
  'task.day.sun',
  'task.day.mon',
  'task.day.tue',
  'task.day.wed',
  'task.day.thu',
  'task.day.fri',
  'task.day.sat',
];

const getTodayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type ExclusionRange = {
  startDate: string;
  endDate: string;
};

type FormData = {
  title: string;
  description: string;
  recurrence: RecurrenceType;
  recurrenceDays: number[];
  monthlyKind: MonthlyKind | null;
  recurrenceMonthDays: number[];
  recurrenceOrdinal: number | null;
  occurrenceDate: string;
  startDate: string;
  endDate: string;
  exclusionRanges: ExclusionRange[];
  dueTime: string;
  requirePhoto: boolean;
  notifyEmployer: boolean;
  remindHelperBeforeDue: boolean;
};

const initialFormData: FormData = {
  title: '',
  description: '',
  recurrence: RECURRENCE_TYPE.NONE,
  recurrenceDays: [],
  monthlyKind: null,
  recurrenceMonthDays: [],
  recurrenceOrdinal: null,
  occurrenceDate: '',
  startDate: getTodayIsoDate(),
  endDate: '',
  exclusionRanges: [],
  dueTime: '',
  requirePhoto: true,
  notifyEmployer: true,
  remindHelperBeforeDue: false,
};

export const TaskFormPage = () => {
  const { t } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  // Fetch task data for edit mode
  useEffect(() => {
    if (!isEdit || !id) return;

    const fetchTask = async () => {
      setFetchLoading(true);
      try {
        const response = await taskApi.get(id);
        if (response.success && response.data) {
          const task = response.data;
          if (task.taskKind === TASK_KIND.INVENTORY) {
            navigate(`/tasks/${id}/edit/inventory`, { replace: true });
            return;
          }
          if (task.taskGroupId) {
            const editPath =
              task.taskKind === TASK_KIND.PICKUP_DROPOFF
                ? `/tasks/groups/${task.taskGroupId}/edit/pickup-dropoff`
                : task.taskKind === TASK_KIND.MEDICATION_REMINDER
                  ? `/tasks/groups/${task.taskGroupId}/edit/medication-reminder`
                  : `/tasks/groups/${task.taskGroupId}/edit/pickup-dropoff`;
            navigate(editPath, { replace: true });
            return;
          }
          setFormData({
            title: task.title,
            description: task.description,
            recurrence: task.recurrence,
            recurrenceDays: task.recurrenceDays,
            monthlyKind: task.monthlyKind ?? null,
            recurrenceMonthDays: task.recurrenceMonthDays ?? [],
            recurrenceOrdinal: task.recurrenceOrdinal ?? null,
            occurrenceDate: task.occurrenceDate,
            startDate: task.startDate || getTodayIsoDate(),
            endDate: task.endDate,
            exclusionRanges: task.exclusionRanges ?? [],
            dueTime: task.dueTime,
            requirePhoto: task.requirePhoto,
            notifyEmployer: task.notifyEmployer,
            remindHelperBeforeDue: isHelperReminderEnabled(task.helperReminderMinutesBefore),
          });
        } else {
          setError(t('task.review.loadError'));
        }
      } catch {
        setError(t('task.review.loadError'));
      } finally {
        setFetchLoading(false);
      }
    };

    fetchTask();
  }, [isEdit, id, navigate, t]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleRecurrenceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as RecurrenceType;
    setFormData((prev) => {
      if (value === RECURRENCE_TYPE.MONTHLY) {
        return {
          ...prev,
          recurrence: value,
          ...emptyMonthlySchedulePatch(MONTHLY_KIND.DAY),
          occurrenceDate: '',
          startDate: prev.startDate || getTodayIsoDate(),
          endDate: prev.endDate,
          exclusionRanges: prev.exclusionRanges,
        };
      }
      return {
        ...prev,
        recurrence: value,
        recurrenceDays:
          value === RECURRENCE_TYPE.NONE || value === RECURRENCE_TYPE.DAILY
            ? []
            : prev.recurrenceDays,
        monthlyKind: null,
        recurrenceMonthDays: [],
        recurrenceOrdinal: null,
        occurrenceDate: value === RECURRENCE_TYPE.NONE ? prev.occurrenceDate : '',
        startDate: value === RECURRENCE_TYPE.NONE ? '' : prev.startDate || getTodayIsoDate(),
        endDate: value === RECURRENCE_TYPE.NONE ? '' : prev.endDate,
        exclusionRanges: value === RECURRENCE_TYPE.NONE ? [] : prev.exclusionRanges,
      };
    });
  };

  const handleDayToggle = (day: number) => {
    setFormData((prev) => {
      const days = prev.recurrenceDays.includes(day)
        ? prev.recurrenceDays.filter((d) => d !== day)
        : [...prev.recurrenceDays, day].sort();
      return { ...prev, recurrenceDays: days };
    });
  };

  const handleExclusionRangeChange = (
    index: number,
    field: 'startDate' | 'endDate',
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      exclusionRanges: prev.exclusionRanges.map((range, rangeIndex) =>
        rangeIndex === index ? { ...range, [field]: value } : range
      ),
    }));
  };

  const handleAddExclusionRange = () => {
    setFormData((prev) => ({
      ...prev,
      exclusionRanges: [...prev.exclusionRanges, { startDate: '', endDate: '' }],
    }));
  };

  const handleRemoveExclusionRange = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      exclusionRanges: prev.exclusionRanges.filter((_, rangeIndex) => rangeIndex !== index),
    }));
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setError(null);

      try {
        const payload = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          recurrence: formData.recurrence,
          recurrenceDays: formData.recurrenceDays,
          monthlyKind: formData.monthlyKind,
          recurrenceMonthDays: formData.recurrenceMonthDays,
          recurrenceOrdinal: formData.recurrenceOrdinal,
          occurrenceDate:
            formData.recurrence === RECURRENCE_TYPE.NONE ? formData.occurrenceDate : '',
          startDate:
            formData.recurrence === RECURRENCE_TYPE.NONE ? '' : formData.startDate,
          endDate:
            formData.recurrence === RECURRENCE_TYPE.NONE ? '' : formData.endDate,
          exclusionRanges:
            formData.recurrence === RECURRENCE_TYPE.NONE
              ? []
              : formData.exclusionRanges.filter((range) => range.startDate && range.endDate),
          dueTime: formData.dueTime,
          requirePhoto: formData.requirePhoto,
          notifyEmployer: formData.notifyEmployer,
          remindHelperBeforeDue:
            formData.dueTime.trim() !== '' && formData.remindHelperBeforeDue,
        };

        if (isEdit && id) {
          const response = await taskApi.update(id, payload);
          if (response.success) {
            showSuccessToast(t('toast.task.updateSuccess'));
            navigate('/tasks', { replace: true });
          } else {
            setError(response.error ?? t('task.form.updateError'));
          }
        } else {
          const response = await taskApi.create(payload);
          if (response.success) {
            showSuccessToast(t('toast.task.createSuccess'));
            navigate('/tasks', { replace: true });
          } else {
            setError(response.error ?? t('task.form.createError'));
          }
        }
      } catch (caught) {
        if (isLimitReachedError(caught)) {
          return;
        }
        const fallbackMessage = isEdit ? t('task.form.updateError') : t('task.form.createError');
        if (caught instanceof ApiClientError) {
          setError(mapApiClientErrorToMessage(caught, t) ?? caught.message ?? fallbackMessage);
          return;
        }
        setError(caught instanceof Error ? caught.message : fallbackMessage);
      } finally {
        setLoading(false);
      }
    },
    [formData, isEdit, id, navigate, showSuccessToast, t]
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };
  const renderClearDateButton = (onClear: () => void, ariaLabel: string) => (
    <button
      type="button"
      onClick={onClear}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
      aria-label={ariaLabel}
    >
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l8 8M14 6l-8 8" />
      </svg>
    </button>
  );

  const showDaysSelector =
    formData.recurrence === RECURRENCE_TYPE.WEEKLY ||
    formData.recurrence === RECURRENCE_TYPE.BIWEEKLY;
  const isMonthly = formData.recurrence === RECURRENCE_TYPE.MONTHLY;
  const isOneTimeTask = formData.recurrence === RECURRENCE_TYPE.NONE;

  if (fetchLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <header className="z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {isEdit ? t('task.form.editTitle') : t('task.form.createTitle')}
            </h1>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="mx-auto max-w-lg px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              {t('task.form.titleLabel')}
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              maxLength={100}
              placeholder={t('task.form.titlePlaceholder')}
              className={TASK_FORM_FIELD_CLASS}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              {t('task.form.descLabelOptional')}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              maxLength={1000}
              placeholder={t('task.form.descPlaceholder')}
              className={`${TASK_FORM_FIELD_CLASS} resize-none`}
            />
          </div>

          {/* Recurrence */}
          <div>
            <label htmlFor="recurrence" className="block text-sm font-medium text-gray-700">
              {t('task.form.recurrenceLabel')}
            </label>
            <select
              id="recurrence"
              name="recurrence"
              value={formData.recurrence}
              onChange={handleRecurrenceChange}
              className={TASK_FORM_FIELD_CLASS}
            >
              <option value={RECURRENCE_TYPE.NONE}>{t('task.recurrence.none')}</option>
              <option value={RECURRENCE_TYPE.DAILY}>{t('task.recurrence.daily')}</option>
              <option value={RECURRENCE_TYPE.WEEKLY}>{t('task.recurrence.weekly')}</option>
              <option value={RECURRENCE_TYPE.BIWEEKLY}>{t('task.recurrence.biweekly')}</option>
              <option value={RECURRENCE_TYPE.MONTHLY}>{t('task.recurrence.monthly')}</option>
            </select>
          </div>

          {isMonthly ? (
            <TaskMonthlyScheduleControls
              t={t}
              idPrefix="task-monthly"
              monthlyKind={formData.monthlyKind}
              recurrenceDays={formData.recurrenceDays}
              recurrenceMonthDays={formData.recurrenceMonthDays}
              recurrenceOrdinal={formData.recurrenceOrdinal}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            />
          ) : null}

          {/* Days Selector */}
          {showDaysSelector && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('task.form.daysLabel')}
              </label>
              <p className="mt-1 text-xs text-gray-500">{t('task.form.daysHint')}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    onKeyDown={handleKeyDown(() => handleDayToggle(day))}
                    tabIndex={0}
                    className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formData.recurrenceDays.includes(day)
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={formData.recurrenceDays.includes(day)}
                    aria-label={t(DAY_KEYS[day])}
                  >
                    {t(DAY_KEYS[day])}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isOneTimeTask ? (
            <div>
              <label htmlFor="occurrenceDate" className="block text-sm font-medium text-gray-700">
                {t('task.form.occurrenceDateLabel')}
              </label>
              <IsoDatePicker
                id="occurrenceDate"
                ariaLabel={t('task.form.occurrenceDateLabel')}
                value={formData.occurrenceDate}
                minDate={isEdit ? undefined : getTodayIsoDate()}
                onChange={(next) => setFormData((prev) => ({ ...prev, occurrenceDate: next }))}
                valueFormat="iso"
                className="mt-1 block w-full"
              />
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  {t('task.form.startDateLabel')}
                </label>
                <IsoDatePicker
                  id="startDate"
                  ariaLabel={t('task.form.startDateLabel')}
                  value={formData.startDate}
                  maxDate={formData.endDate || undefined}
                  onChange={(next) => setFormData((prev) => ({ ...prev, startDate: next }))}
                  valueFormat="iso"
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                  {t('task.form.endDateLabelOptional')}
                </label>
                <div className="mt-1 flex min-w-0 items-center gap-2">
                  <IsoDatePicker
                    id="endDate"
                    ariaLabel={t('task.form.endDateLabelOptional')}
                    value={formData.endDate}
                    minDate={formData.startDate || undefined}
                    onChange={(next) => setFormData((prev) => ({ ...prev, endDate: next }))}
                    valueFormat="iso"
                    className="min-w-0 flex-1"
                  />
                  {formData.endDate !== ''
                    ? renderClearDateButton(
                      () => setFormData((prev) => ({ ...prev, endDate: '' })),
                      t('task.form.endDateLabel'),
                    )
                    : null}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('task.form.excludeRangesLabel')}
                  </label>
                  <button
                    type="button"
                    onClick={handleAddExclusionRange}
                    className="rounded-lg border border-primary-300 bg-white px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {t('task.form.addExcludeRange')}
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {formData.exclusionRanges.map((range, index) => (
                    <div key={`exclude-range-${index}`} className="rounded-lg border border-gray-200 p-3">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <IsoDatePicker
                            id={`task-exclude-start-${index}`}
                            ariaLabel={t('task.form.excludeStartDateLabel')}
                            value={range.startDate}
                            maxDate={range.endDate || undefined}
                            onChange={(next) => handleExclusionRangeChange(index, 'startDate', next)}
                            valueFormat="iso"
                            className="min-w-0 flex-1"
                          />
                          {range.startDate !== ''
                            ? renderClearDateButton(
                              () => handleExclusionRangeChange(index, 'startDate', ''),
                              t('task.form.excludeStartDateLabel'),
                            )
                            : null}
                        </div>
                        <div className="flex min-w-0 items-center gap-2">
                          <IsoDatePicker
                            id={`task-exclude-end-${index}`}
                            ariaLabel={t('task.form.excludeEndDateLabel')}
                            value={range.endDate}
                            minDate={range.startDate || undefined}
                            onChange={(next) => handleExclusionRangeChange(index, 'endDate', next)}
                            valueFormat="iso"
                            className="min-w-0 flex-1"
                          />
                          {range.endDate !== ''
                            ? renderClearDateButton(
                              () => handleExclusionRangeChange(index, 'endDate', ''),
                              t('task.form.excludeEndDateLabel'),
                            )
                            : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveExclusionRange(index)}
                        className="mt-2 rounded-lg border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400"
                      >
                        {t('task.form.removeExcludeRange')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Due Time */}
          <div>
            <label htmlFor="dueTime" className="block text-sm font-medium text-gray-700">
              {t('task.form.dueTimeLabelOptional')}
            </label>
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <NativeTimeInput
                id="dueTime"
                name="dueTime"
                value={formData.dueTime}
                onChange={handleInputChange}
                placeholder={t('task.form.dueTimePlaceholder')}
                wrapperClassName="min-w-0 flex-1"
                className={`${TASK_FORM_FIELD_BASE_CLASS} appearance-none`}
              />
              {formData.dueTime !== ''
                ? renderClearDateButton(
                  () => setFormData((prev) => ({ ...prev, dueTime: '' })),
                  t('task.form.dueTimeLabel'),
                )
                : null}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="requirePhoto"
              name="requirePhoto"
              checked={formData.requirePhoto}
              onChange={handleInputChange}
              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="requirePhoto" className="text-sm font-medium text-gray-700">
              {t('task.form.requirePhotoLabel')}
            </label>
          </div>

          {/* Notify Employer */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="notifyEmployer"
              name="notifyEmployer"
              checked={formData.notifyEmployer}
              onChange={handleInputChange}
              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="notifyEmployer" className="text-sm font-medium text-gray-700">
              {t('task.form.notifyLabel')}
            </label>
          </div>

          <HelperDueReminderCheckbox
            id="remindHelperBeforeDue"
            checked={formData.remindHelperBeforeDue}
            disabled={formData.dueTime.trim() === ''}
            onChange={(remindHelperBeforeDue) =>
              setFormData((prev) => ({ ...prev, remindHelperBeforeDue }))
            }
            t={t}
          />

          {/* Error */}
          {error && (
            <div className="rounded-xl bg-red-50 p-4 text-center">
              <p className="text-sm text-red-600" role="alert">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4">
            <AppButton
              type="submit"
              variant="primary"
              disabled={loading || !formData.title.trim()}
              className="flex-1 shadow-md"
            >
              {loading ? t('task.form.submitting') : t('task.form.submit')}
            </AppButton>
            <AppButton type="button" variant="secondary" onClick={handleBack}>
              {t('task.form.cancel')}
            </AppButton>
          </div>
        </form>
      </main>
    </div>
  );
};
